/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [false]
*/

import {
    DropDown,
    DynamicListRowWrapperDefault,
    DynamicListWrapperProps,
    Pending,
    requiredRule,
    useErrorState,
    useUpdateEffect,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import { getOutletsSettingsTypes } from 'axios/outletsApi';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import { datetimeFormat } from 'constans/dateFormat';
import useApi from 'hooks/useApi';
import { Dictionary } from 'lodash';
import { OutletSettingsList } from 'models/api/outlet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, Row } from 'reactstrap';
import GetOptions from 'utils/options';
import { OutletFormType } from './OutletChangeModal';
import TwoColumnWrapper from './TwoColumnWrapper';

type TabSettingsProps = {
    isOpen?: boolean;
    formHasRole: boolean;
};

const TabSettings = ({ isOpen = false, formHasRole }: TabSettingsProps) => {
    const { handleRequestError } = useErrorState();
    const { t, i18n } = useTranslation();
    const api = useApi();
    const { watch, setValue } = useFormContext<OutletFormType>();
    const useDefaultSettings = watch('settings.isUseDefault');

    useUpdateEffect(() => {
        setValue('settings.list', [{ id: null, type: '', value: '' }]);
    }, [useDefaultSettings]);

    const [hasOpen, setHasOpen] = useState(isOpen);
    useEffect(() => {
        setHasOpen((val) => val || isOpen);
    }, [isOpen]);

    const { isFetching: outletsSettingsTypesFetching, data: outletsSettingsTypes } = useQuery(
        ['OutletsSettingsTypes', i18n.language],
        () => getOutletsSettingsTypes(api),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const { isFetching: tradeOrgSettingFetching, data: tradeOrgSettingTypes } = useQuery(
        ['TradeOrgSettingTypes', i18n.language],
        () => getSelectList(api, ['TradeOrgSettingTypes'], i18n.language),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <Form
                {...props}
                tradeOrgSettingTypes={
                    tradeOrgSettingTypes ? tradeOrgSettingTypes['TradeOrgSettingTypes'] : []
                }
                tradeOrgSettingTypesFetching={tradeOrgSettingFetching}
                formHasRole={formHasRole}
                outletSettingsTypesFetching={outletsSettingsTypesFetching}
                outletSettingsTypes={outletsSettingsTypes || {}}
            />
        ),
        [
            tradeOrgSettingTypes,
            tradeOrgSettingFetching,
            outletsSettingsTypesFetching,
            outletsSettingsTypes,
            formHasRole,
        ]
    );

    const wrapper = useCallback(
        (props: DynamicListWrapperProps) => (
            <TwoColumnWrapper
                {...props}
                col1Label="BackOfficeSettingType"
                col2Label="BackOfficeValue"
            />
        ),
        []
    );

    return (
        <>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        id="settingsIsUseDefault"
                        type="checkbox"
                        name="settings.isUseDefault"
                        label={t('BackOfficeUseDefaultSettings')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            <hr />
            {!useDefaultSettings && (
                <Row>
                    <Col>
                        <ValidatedDynamicList<OutletFormType, OutletSettingsList>
                            formComponent={formComponent}
                            wrapper={wrapper}
                            prepend={false}
                            emptyRecord={{
                                id: null,
                                type: '',
                                value: '',
                            }}
                            name="settings.list"
                            compareFunc={(a, b) => a?.type === b?.type}
                            rowWrapper={(props) => (
                                <DynamicListRowWrapperDefault
                                    {...props}
                                    disabled={props.disabled || props.idx === 0}
                                />
                            )}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

const Form = ({
    idx,
    fieldId,
    checkDouble,
    tradeOrgSettingTypes,
    tradeOrgSettingTypesFetching,
    formHasRole,
    outletSettingsTypes,
    outletSettingsTypesFetching,
}: ValidatedDynamicListFormComponentProps & {
    tradeOrgSettingTypes: DropDown[];
    tradeOrgSettingTypesFetching: boolean;
    formHasRole: boolean;
    outletSettingsTypes: Dictionary<string>;
    outletSettingsTypesFetching: boolean;
}) => {
    const { t } = useTranslation();
    const { watch, setValue } = useFormContext<OutletFormType>();
    const type = watch(`settings.list.${idx}.type`);
    const settingType = useMemo(() => {
        setValue(`settings.list.${idx}.value`, '');
        return outletSettingsTypes[type];
    }, [type]);

    return (
        <>
            <td>
                <ValidatedInput<OutletFormType>
                    type="select"
                    name={`settings.list.${idx}.type`}
                    label={t('BackOfficeSettingType')}
                    disabled={!formHasRole}
                    rules={{
                        required: requiredRule,
                        validate: () => checkDouble(fieldId) ?? undefined,
                    }}
                    loading={tradeOrgSettingTypesFetching || outletSettingsTypesFetching}
                    options={GetOptions(tradeOrgSettingTypes, true, false)}
                />
            </td>
            <td>
                {outletSettingsTypesFetching ? (
                    <Pending />
                ) : settingType === 'datetime' ? (
                    <BOValidatedDatePicker<OutletFormType>
                        name={`settings.list.${idx}.value`}
                        inputProps={{
                            label: t('BackOfficeValue'),
                        }}
                        dateFormat={datetimeFormat}
                        rules={{
                            required: requiredRule,
                        }}
                    />
                ) : (
                    <ValidatedInput<OutletFormType>
                        type={
                            settingType === 'bool'
                                ? 'checkbox'
                                : settingType === 'number'
                                  ? 'number'
                                  : 'text'
                        }
                        name={`settings.list.${idx}.value`}
                        label={t('BackOfficeValue')}
                        disabled={!formHasRole}
                        rules={{
                            required: requiredRule,
                        }}
                    />
                )}
            </td>
        </>
    );
};

export default TabSettings;
